import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus, Tag, X } from "lucide-react";

import { api } from "../api/client";
import { useLabels } from "../api/queries";
import type { Label, LabelType, Proposal } from "../api/types";

export type EntityKind =
  | "panneau"
  | "produit"
  | "commande"
  | "commande_line"
  | "mouvement";

const FAMILY_LABEL: Record<LabelType, string> = {
  statut: "Statut",
  tag: "Tags",
  motif: "Motifs",
};
const FAMILY_ORDER: LabelType[] = ["statut", "tag", "motif"];

const COLOR_PALETTE = [
  { name: "orange", hex: "#F39200" },
  { name: "red", hex: "#E63946" },
  { name: "green", hex: "#2A9D58" },
  { name: "pink", hex: "#E91E63" },
  { name: "gray", hex: "#9b9b9b" },
];

function chipBg(color: string): string {
  return COLOR_PALETTE.find((c) => c.name === color)?.hex ?? "#9b9b9b";
}

interface Props {
  kind: EntityKind;
  entityId: string;
  organisation: string;
  /** Compact rendering (used in dense table cells). */
  compact?: boolean;
  /** Optional pending `label_for_entity` proposals. The component
   *  filters in-place by `entity_kind === kind && entity_id === entityId`
   *  so callers may pass the full panneau-scoped pending list. */
  pending?: Proposal[];
  /** Panneau id whose proposals these are — needed to invalidate the
   *  panneau detail cache after Valider/Refuser. Only required when
   *  `pending` is non-empty. */
  panneauId?: string;
}

/**
 * Reusable label chip cluster for any entity (produit / commande /
 * commande_line / mouvement). Same UX vocabulary as the panneau
 * LabelsChip — pill list + "+" popover — but driven by the generic
 * `/api/labels/attach/{kind}/{id}/{lid}` endpoint instead of a
 * panneau-specific route.
 */
export function EntityLabels({
  kind,
  entityId,
  organisation,
  compact = false,
  pending = [],
  panneauId,
}: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Filter pending proposals to those scoped to this entity.
  const pendingForEntity = pending.filter(
    (p) =>
      p.type === "label_for_entity" &&
      p.payload.entity_kind === kind &&
      p.payload.entity_id === entityId,
  );

  const attachedKey = ["entityLabels", kind, entityId] as const;
  const attached = useQuery<Label[]>({
    queryKey: attachedKey,
    queryFn: () =>
      api<Label[]>(
        `/api/labels/for/${kind}/${encodeURIComponent(entityId)}`,
      ),
    staleTime: 5_000,
  });
  const allLabels = useLabels(organisation);

  const attach = useMutation({
    mutationFn: (labelId: string) =>
      api<void>(
        `/api/labels/attach/${kind}/${encodeURIComponent(entityId)}/${labelId}`,
        { method: "POST" },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: attachedKey }),
  });
  const detach = useMutation({
    mutationFn: (labelId: string) =>
      api<void>(
        `/api/labels/attach/${kind}/${encodeURIComponent(entityId)}/${labelId}`,
        { method: "DELETE" },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: attachedKey }),
  });

  // Per-proposal validate / reject. We don't reuse the panneau hooks
  // because the panneau id is optional here (some entities — e.g. a
  // produit chip in the commandes page — aren't on a panneau view).
  const validateProp = useMutation({
    mutationFn: (proposalId: string) =>
      api<{ committed: string[]; skipped: string[] }>(
        `/api/panneaux/${panneauId}/validate/${proposalId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: attachedKey });
      if (panneauId)
        qc.invalidateQueries({ queryKey: ["panneau", panneauId] });
    },
  });
  const rejectProp = useMutation({
    mutationFn: (proposalId: string) =>
      api<void>(
        `/api/panneaux/${panneauId}/reject/${proposalId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      if (panneauId)
        qc.invalidateQueries({ queryKey: ["panneau", panneauId] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current && !popoverRef.current.contains(t) &&
        buttonRef.current && !buttonRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const attachedIds = new Set((attached.data ?? []).map((l) => l.id));
  const grouped: Record<LabelType, Label[]> = { statut: [], tag: [], motif: [] };
  for (const l of allLabels.data ?? []) grouped[l.label_type].push(l);

  return (
    <div className="relative inline-flex flex-wrap items-center gap-1">
      {(attached.data ?? []).map((l) => (
        <span
          key={l.id}
          className={`inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded-full text-[10px] text-white ${
            l.label_type === "statut" ? "ring-2 ring-offset-1 ring-ink/30 dark:ring-white/30" : ""
          }`}
          style={{ backgroundColor: chipBg(l.color) }}
          title={`${FAMILY_LABEL[l.label_type]}: ${l.name}`}
        >
          <span>{l.name}</span>
          <button
            type="button"
            onClick={() => detach.mutate(l.id)}
            disabled={detach.isPending}
            className="rounded-full hover:bg-black/20 dark:hover:bg-white/20 px-0.5"
            aria-label={`Détacher ${l.name}`}
          >
            <X className="h-2 w-2" />
          </button>
        </span>
      ))}
      {pendingForEntity.map((p) => {
        const name = String(p.payload.label_name ?? "");
        const color = String(p.payload.color ?? "gray");
        const isValidating =
          validateProp.isPending && validateProp.variables === p.id;
        const isRejecting =
          rejectProp.isPending && rejectProp.variables === p.id;
        return (
          <span
            key={p.id}
            className="inline-flex items-center gap-0.5 pl-1.5 pr-0.5 py-0.5 rounded-full text-[10px] italic opacity-70 border border-dashed bg-white/60 dark:bg-surface-dark/60"
            style={{ borderColor: chipBg(color), color: chipBg(color) }}
            title={`Proposition de label : ${name} — « ${p.source_quote} »`}
          >
            <span className="uppercase tracking-wide text-[10px] opacity-80">
              à valider
            </span>
            <span>{name}</span>
            <button
              type="button"
              onClick={() => validateProp.mutate(p.id)}
              disabled={isValidating || isRejecting || !panneauId}
              className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 px-0.5"
              aria-label={`Valider le label ${name}`}
              title={panneauId ? "Valider" : "Valider depuis le panneau"}
            >
              {isValidating ? (
                <Loader2 className="h-2 w-2 animate-spin" />
              ) : (
                <Check className="h-2 w-2" />
              )}
            </button>
            <button
              type="button"
              onClick={() => rejectProp.mutate(p.id)}
              disabled={isValidating || isRejecting || !panneauId}
              className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 px-0.5"
              aria-label={`Refuser le label ${name}`}
              title={panneauId ? "Refuser" : "Refuser depuis le panneau"}
            >
              {isRejecting ? (
                <Loader2 className="h-2 w-2 animate-spin" />
              ) : (
                <X className="h-2 w-2" />
              )}
            </button>
          </span>
        );
      })}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${compact ? "h-4 w-4" : "h-5 w-5"} rounded-full border border-dashed border-black/30 dark:border-white/30 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:border-ink dark:hover:border-white flex items-center justify-center`}
        aria-label="Ajouter un label"
      >
        <Plus className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full mt-1.5 z-30 w-56 bg-white dark:bg-surface-dark rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-2"
          role="dialog"
          aria-label="Choisir un label"
        >
          {allLabels.isLoading && (
            <div className="text-xs text-muted dark:text-muted-dark px-2 py-1.5 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Chargement…
            </div>
          )}
          {allLabels.data && allLabels.data.length === 0 && (
            <div className="text-xs text-muted dark:text-muted-dark px-2 py-1.5">
              Aucun label dans cette organisation. Créez-en depuis un panneau.
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {FAMILY_ORDER.map((family) => {
              const rows = grouped[family];
              if (rows.length === 0) return null;
              return (
                <section key={family} className="mb-1">
                  <header className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted dark:text-muted-dark">
                    {FAMILY_LABEL[family]}
                  </header>
                  <ul>
                    {rows.map((l) => {
                      const isAttached = attachedIds.has(l.id);
                      return (
                        <li key={l.id}>
                          <button
                            type="button"
                            onClick={() =>
                              isAttached
                                ? detach.mutate(l.id)
                                : attach.mutate(l.id)
                            }
                            disabled={attach.isPending || detach.isPending}
                            className="w-full text-left px-2 py-1 rounded hover:bg-canvas dark:hover:bg-canvas-dark flex items-center gap-2 text-sm"
                          >
                            <span
                              className="h-3 w-3 rounded-full shrink-0"
                              style={{ backgroundColor: chipBg(l.color) }}
                            />
                            <span className="flex-1 truncate">{l.name}</span>
                            {isAttached && (
                              <Tag className="h-3 w-3 text-ink dark:text-ink-dark" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
